<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                Promotion List
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row class="py-2">
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search"
                      single-line
                      hide-details
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      class="pl-2"
                      clearable
                    ></v-text-field>
                  </v-col>

                  <v-col md="5" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group
                        variant="outlined"
                        density="compact"
                        size="x-small"
                        divided
                      >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="success"
                          @click="exportToCSV"
                        >CSV</v-btn>
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="primary"
                          @click="exportToExcel"
                        >EXCEL</v-btn>
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="red"
                          @click="exportToPDF"
                        >PDF</v-btn>
                      </v-btn-group>
                    </div>
                  </v-col>

                  <v-col md="3" cols="12">
                    <div class="text-right pr-2">
                      <v-btn
                        v-if="$insertAccess($route.fullPath)"
                        @click="goTo('/hrm/add-promotion')"
                        color="primary"
                        variant="tonal"
                        prepend-icon="mdi mdi-plus"
                        rounded="xl"
                      >
                        Add Promotion
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>

                <v-card class="overflow-x-auto">
                  <v-data-table
                    :headers="headers"
                    :items="promotions"
                    :loading="loading"
                    v-model:items-per-page="itemsPerPage"
                    :search="search"
                    :hover="true"
                    density="compact"
                    class="min-w-[1200px]"
                  >
                    <template v-slot:item.index="{ index }">
                      {{ ++index }}
                    </template>

                    <template v-slot:item.employee_name="{ item }">
                      <span class="font-weight-medium">{{ item.employee_name }}</span>
                    </template>

                    <template v-slot:item.promotion_type="{ item }">
                      <v-chip
                        :color="getPromotionTypeColor(item.promotion_type)"
                        size="small"
                        variant="tonal"
                        class="text-capitalize"
                      >
                        {{ getPromotionTypeText(item.promotion_type) }}
                      </v-chip>
                    </template>

                    <template v-slot:item.from_designation="{ item }">
                      <span class="text-caption">{{ item.from_designation }}</span>
                    </template>

                    <template v-slot:item.to_designation="{ item }">
                      <span class="font-weight-medium">{{ item.to_designation }}</span>
                    </template>

                    <template v-slot:item.after_gross_salary="{ item }">
                      <span class="font-weight-medium">৳{{ formatNumber(item.after_gross_salary) }}</span>
                    </template>

                    <template v-slot:item.promotion_date="{ item }">
                      <span class="text-caption">{{ formatDate(item.promotion_date) }}</span>
                    </template>
                    
                    <template v-slot:item.effect_date="{ item }">
                      <span class="text-caption">{{ formatDate(item.effect_date) }}</span>
                    </template>

                    <template v-slot:item.status="{ item }">
                      <v-chip
                        :color="getStatusColor(item.status)"
                        size="small"
                        variant="tonal"
                        class="text-capitalize"
                      >
                        {{ getStatusText(item.status) }}
                      </v-chip>
                    </template>

                    <template v-slot:item.actions="{ item }">
                      <v-btn
                        @click="editPromotion(item)"
                        icon="mdi-pencil"
                        size="small"
                        color="primary"
                        variant="text"
                        title="Edit Promotion"
                      ></v-btn>
                    </template>
                  </v-data-table>
                </v-card>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>

  </v-app>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { toast } from 'vue3-toastify'
import { hrmService } from '@/services/hrmService'

// Router and Store
const router = useRouter()
const store = useStore()

// Get Vue instance for export functions
const instance = getCurrentInstance()

// Reactive data
const panel = ref([0])
const search = ref('')
const loading = ref(false)
const itemsPerPage = ref(10)
const promotions = ref([])

// Table headers
const headers = ref([
  { title: '#SL', key: 'index', sortable: false, align: 'center' },
  { title: 'Employee Name', key: 'employee_name', sortable: true, align: 'left' },
  { title: 'Ref No', key: 'ref_no', sortable: true, align: 'left' },
  { title: 'Type', key: 'promotion_type', sortable: true, align: 'left' },
  { title: 'From Designation', key: 'from_designation', sortable: true, align: 'left' },
  { title: 'To Designation', key: 'to_designation', sortable: true, align: 'left' },
  { title: 'Promotion Date', key: 'promotion_date', sortable: true, align: 'center' },
  { title: 'Effect Date', key: 'effect_date', sortable: true, align: 'center' },
  { title: 'Gross Salary', key: 'after_gross_salary', sortable: true, align: 'right' },
  { title: 'Status', key: 'status', sortable: true, align: 'center' },
  { title: 'Actions', key: 'actions', sortable: false, align: 'center', width: '80px' }
])

// Options
const promotionTypes = ref([
  { text: 'Regular', value: 'regular' },
  { text: 'Merit', value: 'merit' },
  { text: 'Seniority', value: 'seniority' },
  { text: 'Special', value: 'special' },
  { text: 'Lateral', value: 'lateral' }
])

const statusOptions = ref([
  { text: 'Active', value: 'active' },
  { text: 'Inactive', value: 'inactive' }
])

// Methods
const loadPromotions = async () => {
  loading.value = true
  try {
    const response = await hrmService.getPromotionList({
      search: search.value,
      order_by: 'promotion_date',
      order_direction: 'desc'
    })
    
    if (response.success) {
      promotions.value = response.data.map(promotion => ({
        id: promotion.id,
        ref_no: promotion.ref_no,
        employee_name: promotion.employee?.name || 'N/A',
        employee_type: promotion.employee?.employee_type?.name || 'N/A',
        promotion_type: promotion.promotion_type,
        salary_increase: promotion.salary_increment_amount,
        status: promotion.status,
        approval_status: promotion.approval_status,
        promotion_date: promotion.promotion_date,
        effect_date: promotion.effect_date,
        from_department: promotion.current_department?.name || 'N/A',
        to_department: promotion.new_department?.name || 'N/A',
        from_designation: promotion.current_designation?.name || 'N/A',
        to_designation: promotion.new_designation?.name || 'N/A',
        employee: promotion.employee,
        // Include after salary data for editing
        after_package: promotion.after_package,
        after_gross_salary: promotion.after_gross_salary,
        after_basic: promotion.after_basic,
        after_house_rent: promotion.after_house_rent,
        after_medical: promotion.after_medical,
        after_education: promotion.after_education,
        after_mobile: promotion.after_mobile,
        after_transport: promotion.after_transport,
        after_technical: promotion.after_technical,
        after_festival: promotion.after_festival,
        after_tiffin: promotion.after_tiffin,
        after_wash: promotion.after_wash,
        after_incentive: promotion.after_incentive,
        after_others: promotion.after_others,
        after_tax_deduct: promotion.after_tax_deduct,
        after_pf: promotion.after_pf,
        after_car_use: promotion.after_car_use,
        after_gas_bill: promotion.after_gas_bill,
        after_electricity_bill: promotion.after_electricity_bill,
        after_telephone_bill: promotion.after_telephone_bill,
        after_insurance: promotion.after_insurance,
        after_others_deduct: promotion.after_others_deduct
      }))
    } else {
      toast.error(response.message || 'Failed to load promotions')
    }
  } catch (error) {
    console.error('Error loading promotions:', error)
    toast.error('Failed to load promotions')
  } finally {
    loading.value = false
  }
}

const editPromotion = async (item) => {
  try {
    // Try to fetch complete promotion details
    const response = await hrmService.getPromotionById(item.id)
    
    if (response.success) {
      store.commit('setEditingPromotion', response.data)
    } else {
      store.commit('setEditingPromotion', item)
    }
  } catch (error) {
    console.error('API call failed, using basic data:', error)
    store.commit('setEditingPromotion', item)
  }
  
  router.push('/hrm/add-promotion')
}


const goTo = (path) => {
  try {
    // Clear any editing promotion state when navigating to add new promotion
    if (path === '/hrm/add-promotion') {
      store.commit('clearEditingPromotion')
    }
    router.push(path)
  } catch (error) {
    console.error('Navigation error:', error)
  }
}

// Export functions
const exportToPDF = () => {
  let serialNumber = 1
  const columns = [
    "#", 
    "Employee Name", 
    "REF NO", 
    "Designation",
    "Employee Type",
    "Promotion Date", 
    "Effect Date", 
    "Gross Salary", 
    "Status"
  ]
  const rows = promotions.value.map((item) => [
    serialNumber++,
    item.employee_name,
    item.ref_no,
    item.to_designation,
    item.employee_type,
    formatDate(item.promotion_date),
    formatDate(item.effect_date),
    formatNumber(item.after_gross_salary),
    getStatusText(item.status)
  ])
  
  // Use Vue instance export functions
  instance.proxy.$exportTableToPDF(columns, rows, "Promotion List", "promotion_list_" + instance.proxy.$fileDate())
}

const exportToExcel = () => {
  const data = [[
    "#", 
    "Employee Name", 
    "REF NO", 
    "Designation",
    "Employee Type",
    "Promotion Date", 
    "Effect Date", 
    "Gross Salary", 
    "Status"
  ]]
  let serialNumber = 1
  
  promotions.value.forEach((item) => {
    data.push([
      serialNumber++,
      item.employee_name,
      item.ref_no,
      item.to_designation,
      item.employee_type,
      formatDate(item.promotion_date),
      formatDate(item.effect_date),
      formatNumber(item.after_gross_salary),
      getStatusText(item.status)
    ])
  })
  
  // Use Vue instance export functions
  instance.proxy.$exportTableToExcel(data, "promotion_list_" + instance.proxy.$fileDate())
}

const exportToCSV = () => {
  const data = [[
    "#", 
    "Employee Name", 
    "REF NO", 
    "Designation",
    "Employee Type",
    "Promotion Date", 
    "Effect Date", 
    "Gross Salary", 
    "Status"
  ]]
  let serialNumber = 1
  
  promotions.value.forEach((item) => {
    data.push([
      serialNumber++,
      item.employee_name,
      item.ref_no,
      item.to_designation,
      item.employee_type,
      formatDate(item.promotion_date),
      formatDate(item.effect_date),
      formatNumber(item.after_gross_salary),
      getStatusText(item.status)
    ])
  })
  
  // Use Vue instance export functions
  instance.proxy.$exportTableToCSV(data, "promotion_list_" + instance.proxy.$fileDate())
}


// Utility functions
const formatNumber = (number) => {
  return new Intl.NumberFormat('en-US').format(number || 0)
}

const formatDate = (date) => {
  if (!date) return '-'
  try {
    // Handle both ISO format and date-only format
    let dateObj
    if (date.includes('T')) {
      // ISO format: 2025-09-14T18:00:00.000000Z
      dateObj = new Date(date)
    } else {
      // Date only format: 2025-09-15
      dateObj = new Date(date + 'T00:00:00')
    }
    
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  } catch (error) {
    console.error('Date formatting error:', error)
    return '-'
  }
}

// Status helper functions
const getStatusColor = (status) => {
  switch (status) {
    case 'active':
      return 'success'
    case 'inactive':
      return 'error'
    default:
      return 'grey'
  }
}

const getStatusText = (status) => {
  switch (status) {
    case 'active':
      return 'Active'
    case 'inactive':
      return 'Inactive'
    default:
      return 'Unknown'
  }
}

const getPromotionTypeText = (type) => {
  switch (type) {
    case 'regular':
      return 'Regular'
    case 'merit':
      return 'Merit'
    case 'seniority':
      return 'Seniority'
    case 'special':
      return 'Special'
    case 'lateral':
      return 'Lateral'
    default:
      return 'Unknown'
  }
}

const getPromotionTypeColor = (type) => {
  const colors = {
    regular: 'blue',
    merit: 'green',
    seniority: 'orange',
    special: 'purple',
    lateral: 'teal'
  }
  return colors[type] || 'grey'
}

const getApprovalStatusText = (status) => {
  const statuses = {
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected'
  }
  return statuses[status] || status
}

const getApprovalStatusColor = (status) => {
  const colors = {
    pending: 'warning',
    approved: 'success',
    rejected: 'error'
  }
  return colors[status] || 'default'
}

// Initialize
onMounted(() => {
  // Clear any editing promotion state when the list loads
  store.commit('clearEditingPromotion')
  loadPromotions()
})
</script>
<style scoped>
.panel-header {
  font-weight: 600;
}

.tab-item {
  border-radius: 8px;
  margin: 2px 8px;
}

.tab-active {
  background-color: rgb(var(--v-theme-primary));
  color: white;
}

.min-w-\[1200px\] {
  min-width: 1200px;
}
</style>

